import { Box, keyframes } from "@mui/material";
import React from "react";
import { getHomePremium } from "../styles/homePremiumStyles";

const mistDrift = keyframes`
  0% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(6%, -5%, 0) scale(1.12); }
  100% { transform: translate3d(-5%, 4%, 0) scale(1.04); }
`;

const hazeSweep = keyframes`
  0% { transform: translateX(-20%) rotate(4deg); }
  100% { transform: translateX(20%) rotate(4deg); }
`;

const bubbleFloat = keyframes`
  0% { transform: translate3d(0, 0, 0); opacity: 0.3; }
  25% { opacity: 0.85; }
  50% { transform: translate3d(var(--dx), calc(var(--dy) * 0.5), 0); opacity: 0.6; }
  100% { transform: translate3d(calc(var(--dx) * 0.4), var(--dy), 0); opacity: 0.15; }
`;

const BUBBLES = [
    { left: "6%", top: "8%", size: 14, duration: "10s", delay: "0s", dx: "16px", dy: "-70px" },
    { left: "12%", top: "42%", size: 20, duration: "12s", delay: "-2s", dx: "10px", dy: "-90px" },
    { left: "8%", top: "72%", size: 12, duration: "11s", delay: "-5s", dx: "-10px", dy: "-65px" },
    { left: "82%", top: "12%", size: 16, duration: "10s", delay: "-1s", dx: "-14px", dy: "-75px" },
    { left: "88%", top: "48%", size: 22, duration: "13s", delay: "-4s", dx: "-8px", dy: "-95px" },
    { left: "78%", top: "78%", size: 13, duration: "11s", delay: "-7s", dx: "12px", dy: "-70px" },
    { left: "42%", top: "18%", size: 10, duration: "9s", delay: "-1s", dx: "8px", dy: "-50px" },
    { left: "55%", top: "88%", size: 18, duration: "13s", delay: "-3s", dx: "-12px", dy: "-100px" },
];

/**
 * Light-blue mist + bubbles as a background layer only.
 * Parent must be `position: relative` with `overflow: hidden`.
 * Keep card/page content at z-index > 0 so title, grid, chips, and buttons stay clear.
 */
export default function HomeLightBlueMist({ isDark = false }) {
    const theme = getHomePremium(isDark);
    const mistBlobs = [
        {
            top: "-20%",
            left: "-25%",
            w: "90%",
            h: "65%",
            background: theme.mistBlobA,
            blur: "28px",
            duration: "15s",
            delay: "0s",
        },
        {
            top: "20%",
            right: "-30%",
            w: "85%",
            h: "60%",
            background: theme.mistBlobB,
            blur: "32px",
            duration: "18s",
            delay: "-5s",
        },
        {
            bottom: "-18%",
            left: "-10%",
            w: "95%",
            h: "55%",
            background: theme.mistBlobC,
            blur: "30px",
            duration: "16s",
            delay: "-9s",
        },
    ];

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                overflow: "hidden",
                pointerEvents: "none",
                borderRadius: "inherit",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: theme.mistWash,
                }}
            />

            {mistBlobs.map((blob, index) => (
                <Box
                    key={`mist-${index}`}
                    sx={{
                        position: "absolute",
                        top: blob.top,
                        left: blob.left,
                        right: blob.right,
                        bottom: blob.bottom,
                        width: blob.w,
                        height: blob.h,
                        background: blob.background,
                        filter: `blur(${blob.blur})`,
                        animation: `${mistDrift} ${blob.duration} ease-in-out infinite alternate`,
                        animationDelay: blob.delay,
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                    }}
                />
            ))}

            <Box
                sx={{
                    position: "absolute",
                    top: "25%",
                    left: "-30%",
                    width: "160%",
                    height: "40%",
                    background: theme.mistHaze,
                    filter: "blur(20px)",
                    animation: `${hazeSweep} 12s ease-in-out infinite alternate`,
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}
            />

            {BUBBLES.map((b, index) => (
                <Box
                    key={`bubble-${index}`}
                    sx={{
                        "--dx": b.dx,
                        "--dy": b.dy,
                        position: "absolute",
                        left: b.left,
                        top: b.top,
                        width: b.size,
                        height: b.size,
                        borderRadius: "50%",
                        background: theme.bubbleFill,
                        border: `1px solid ${theme.bubbleBorder}`,
                        boxShadow: theme.bubbleShadow,
                        animation: `${bubbleFloat} ${b.duration} ease-in-out infinite`,
                        animationDelay: b.delay,
                        "@media (prefers-reduced-motion: reduce)": {
                            animation: "none",
                            opacity: 0.45,
                        },
                    }}
                />
            ))}
        </Box>
    );
}
